import { useCallback, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, RotateCcw } from "lucide-react";

const PLANET_ORDER = ["Sun", "Moon", "Mars", "Mercury", "Jupiter", "Venus", "Saturn", "Rahu", "Ketu"];

const LOCATIONS = [
  { key: "bse", label: "Mumbai (NSE/BSE)", lat: 19.076, lon: 72.8777, tz: "Asia/Kolkata" },
  { key: "nyse", label: "New York (NYSE)", lat: 40.7069, lon: -74.0113, tz: "America/New_York" },
  { key: "lse", label: "London (LSE)", lat: 51.5155, lon: -0.0922, tz: "Europe/London" },
  { key: "tse", label: "Tokyo (TSE)", lat: 35.6812, lon: 139.7671, tz: "Asia/Tokyo" },
  { key: "sse", label: "Shanghai (SSE)", lat: 31.2304, lon: 121.4737, tz: "Asia/Shanghai" },
  { key: "hkex", label: "Hong Kong (HKEX)", lat: 22.2839, lon: 114.1585, tz: "Asia/Hong_Kong" },
];

// Colour badge per sign-relation
const REL_STYLE = {
  exalted:      "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  moolatrikona: "bg-lime-500/20 text-lime-300 border-lime-500/40",
  own:          "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  friend:       "bg-blue-500/20 text-blue-300 border-blue-500/40",
  neutral:      "bg-neutral-500/20 text-neutral-300 border-neutral-500/40",
  enemy:        "bg-rose-500/20 text-rose-300 border-rose-500/40",
  debilitated:  "bg-red-600/25 text-red-300 border-red-600/50",
};
const REL_LABEL = {
  exalted: "Exalted", moolatrikona: "Moola.", own: "Own",
  friend: "Friend", neutral: "Neutral", enemy: "Enemy",
  debilitated: "Debil.",
};

// Colour by Avastha score
const scoreColor = (s) => {
  if (s >= 8) return "text-emerald-300";
  if (s >= 4) return "text-amber-300";
  return "text-rose-400";
};

const RANGE_PRESETS = [
  { key: "month", label: "Month", days: 30 },
  { key: "3month", label: "3 Months", days: 90 },
  { key: "6month", label: "6 Months", days: 180 },
  { key: "year", label: "Year", days: 365 },
  { key: "custom", label: "Custom", days: 0 },
];

function iso(d) { return d.toISOString().slice(0, 10); }

function todayPlus(days) {
  const s = new Date(); const e = new Date();
  e.setDate(e.getDate() + Math.max(0, days - 1));
  return { start: iso(s), end: iso(e) };
}

export default function RangeStrengthsPanel() {
  const initial = todayPlus(30);
  const [preset, setPreset] = useState("month");
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [loc, setLoc] = useState(LOCATIONS[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [planetFilter, setPlanetFilter] = useState("ALL");

  const applyPreset = (key) => {
    setPreset(key);
    const p = RANGE_PRESETS.find((x) => x.key === key);
    if (p && p.days > 0) {
      const r = todayPlus(p.days);
      setStartDate(r.start); setEndDate(r.end);
    }
  };

  const compute = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      const r = await api.rangeStrengths({
        start_date: startDate, end_date: endDate,
        lat: loc.lat, lon: loc.lon, tz: loc.tz,
      });
      setData(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Compute failed");
    }
    setLoading(false);
  }, [startDate, endDate, loc]);

  const downloadPdf = () => {
    setPdfBusy(true);
    fetch(api.rangeStrengthsPdfUrl(), {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        start_date: startDate, end_date: endDate,
        lat: loc.lat, lon: loc.lon, tz: loc.tz,
      }),
    }).then(async (res) => {
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `planet-transits_${startDate}_to_${endDate}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(() => toast.error("PDF failed"))
      .finally(() => setPdfBusy(false));
  };

  // Filter rows by planet selection
  const rows = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const day of data.days) {
      for (const pl of day.planets) {
        if (planetFilter !== "ALL" && pl.planet !== planetFilter) continue;
        out.push({ ...pl, date: day.date, weekday: day.weekday });
      }
    }
    return out;
  }, [data, planetFilter]);

  return (
    <div className="space-y-6" data-testid="range-strengths-panel">
      <div className="border-l-4 border-[#D4AF37] pl-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
          Planet Transit Table · Sayana · 12:30 local
        </div>
        <div className="text-[10px] text-[#6E6B64] font-mono mt-1">
          Degree · Sign · Sign-Relation (Friend/Enemy/Neutral) · Avastha + Score · Ashtakavarga · Shadbala (0-10) + Strength
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">Range</div>
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger data-testid="range-preset-select"
                           className="w-36 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              {RANGE_PRESETS.map((r) => (
                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">From</div>
          <Input data-testid="range-start-input" type="date" value={startDate}
                 onChange={(e) => { setStartDate(e.target.value); setPreset("custom"); }}
                 className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-40" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">To</div>
          <Input data-testid="range-end-input" type="date" value={endDate}
                 onChange={(e) => { setEndDate(e.target.value); setPreset("custom"); }}
                 className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-40" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">Exchange</div>
          <Select value={loc.key} onValueChange={(v) => setLoc(LOCATIONS.find((l) => l.key === v))}>
            <SelectTrigger data-testid="range-location-select"
                           className="w-56 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              {LOCATIONS.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">Planet</div>
          <Select value={planetFilter} onValueChange={setPlanetFilter}>
            <SelectTrigger data-testid="range-planet-filter"
                           className="w-32 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              <SelectItem value="ALL">All planets</SelectItem>
              {PLANET_ORDER.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="range-compute-btn" onClick={compute} disabled={loading}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {loading ? "Computing…" : "Compute"}
        </Button>
        <Button data-testid="range-pdf-btn" onClick={downloadPdf} disabled={!data || loading || pdfBusy}
                variant="ghost"
                className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent border border-[#2A2D35] rounded-sm">
          <Download size={14} className="mr-2" /> {pdfBusy ? "Building…" : "PDF"}
        </Button>
      </div>

      {loading && (
        <div className="text-[#A19E95] font-mono text-sm">
          Sampling planet transits day-by-day…
        </div>
      )}

      {data && !loading && (
        <>
          <div className="text-[10px] font-mono text-[#6E6B64]">
            {data.total_days} day{data.total_days === 1 ? "" : "s"} × {PLANET_ORDER.length} planets ={" "}
            {data.total_days * PLANET_ORDER.length} rows · rendering {rows.length}
          </div>

          {/* Compact per-planet-per-day table */}
          <div className="border border-[#2A2D35] bg-[#181A1F] overflow-x-auto max-h-[70vh]">
            <table className="w-full font-mono text-xs">
              <thead className="bg-[#0F1115] text-[#6E6B64] sticky top-0 z-10">
                <tr>
                  {["Date", "Day", "Planet", "Deg°", "Sign", "Lord",
                    "Relation", "Avastha", "Av-Score", "BAV", "Shadbala", "Strength"].map((h) => (
                    <th key={h} className="px-2 py-2 text-left border-b border-[#2A2D35]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.date + "-" + r.planet}
                      className="border-b border-[#2A2D35]/50 hover:bg-[#0F1115]/40"
                      data-testid={`range-row-${r.date}-${r.planet}`}>
                    <td className="px-2 py-1.5 text-[#A19E95]">{r.date}</td>
                    <td className="px-2 py-1.5 text-[#6E6B64]">{r.weekday.slice(0, 3)}</td>
                    <td className="px-2 py-1.5 text-[#D4AF37] font-semibold">
                      {r.planet}
                      {r.retrograde && <span className="ml-1 text-rose-300 text-[9px]">(R)</span>}
                    </td>
                    <td className="px-2 py-1.5 text-[#E6E1D6]">{r.deg_in_sign.toFixed(2)}°</td>
                    <td className="px-2 py-1.5 text-[#E6E1D6]">{r.sign}</td>
                    <td className="px-2 py-1.5 text-[#A8896C]">{r.sign_lord || "-"}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-2 py-0.5 border rounded-sm text-[10px] ${REL_STYLE[r.sign_relation] || REL_STYLE.neutral}`}>
                        {REL_LABEL[r.sign_relation] || r.sign_relation}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-[#A19E95]">{r.avastha}</td>
                    <td className={`px-2 py-1.5 font-bold ${scoreColor(r.avastha_score)}`}>
                      {r.avastha_score}
                    </td>
                    <td className="px-2 py-1.5 text-[#E6E1D6]">
                      {r.ashtakavarga_score == null ? <span className="text-[#6E6B64]">N/A</span> : r.ashtakavarga_score}
                    </td>
                    <td className={`px-2 py-1.5 font-bold ${r.shadbala_score == null ? "text-[#6E6B64]" : scoreColor(r.shadbala_score)}`}>
                      {r.shadbala_score == null ? "N/A" : r.shadbala_score.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.shadbala_state == null ? <span className="text-[#6E6B64]">N/A</span> : (
                        <span className={`px-2 py-0.5 border rounded-sm text-[10px] ${
                          r.shadbala_state === "Strong" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                          : r.shadbala_state === "Weak" ? "bg-rose-500/20 text-rose-300 border-rose-500/40"
                          : "bg-amber-500/20 text-amber-300 border-amber-500/40"}`}>
                          {r.shadbala_state}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="text-[10px] font-mono text-[#6E6B64] flex flex-wrap gap-3 pt-2">
            <span><span className="inline-block px-1.5 py-0.5 border rounded-sm text-emerald-300 border-emerald-500/40 bg-emerald-500/20">Exalted</span></span>
            <span><span className="inline-block px-1.5 py-0.5 border rounded-sm text-yellow-300 border-yellow-500/40 bg-yellow-500/20">Own</span></span>
            <span><span className="inline-block px-1.5 py-0.5 border rounded-sm text-blue-300 border-blue-500/40 bg-blue-500/20">Friend</span></span>
            <span><span className="inline-block px-1.5 py-0.5 border rounded-sm text-neutral-300 border-neutral-500/40 bg-neutral-500/20">Neutral</span></span>
            <span><span className="inline-block px-1.5 py-0.5 border rounded-sm text-rose-300 border-rose-500/40 bg-rose-500/20">Enemy</span></span>
            <span><span className="inline-block px-1.5 py-0.5 border rounded-sm text-red-300 border-red-600/50 bg-red-600/25">Debilitated</span></span>
            <span className="text-[#A19E95]">· Av-Score: Bala 2 · Kumara 5 · Yuva 10 · Vriddha 5 · Mrita 0</span>
            <span>· BAV: planet's own Ashtakavarga bindus in current sign (0-8)</span>
            <span>· Shadbala: BPHS six-fold strength (0-10) · Strong ≥ 6 · Weak ≤ 4</span>
          </div>
        </>
      )}
    </div>
  );
}
